import { useRef, useState } from 'react'
import { useLvgState } from 'hooks/games/lvg/useLvgState'
import { ASSETS_TYPES, STOCK_MARKET_TIME } from 'constants/assets'
import { isStockMarketOpen } from 'utils'
import { useYoloToast } from 'lib/yoloToasts/useYoloToast'
import { isEmpty } from 'lodash'
import {
  AssetChange,
  FilterBox,
  Header,
  HotPeriod,
  Icon,
  IconSelected,
  MenuSelect,
  Name,
  NameSelected,
  SelectDropdown,
  SelectDropdownItem,
  SelectDropdownTitle,
  SelectedAsset,
  SelectWrapper,
} from './LvgAssetsDropDown.styled'
import { useClickOutside } from 'utils/hooks'
import { useAssetsForDropdown } from './useAssetsForDropdown'

const stockAssetRestrictionToastObj = {
  id: 'warningToast',
  autoClose: false,
  props: {
    message: {
      title: 'Trading with stocks have some restrictions',
      subtitle: (
        <>
          {`Five minutes before the markets close at ${STOCK_MARKET_TIME.closeTimeUtc} UTC, all live and open bids`}{' '}
          <br />
          {`will automatically get closed.`}
        </>
      ),
    },
  },
}
export const LvgAssetsSelector = ({ noSelect }) => {
  const { activeAsset, setActiveAsset } = useLvgState()
  const { hotAssets, coldAssets, featuredAssets, assetsTypes, getAssetWithVariation, setFilter } =
    useAssetsForDropdown()

  const [isSelectOpen, setSelectOpen] = useState(false)

  const dropdownRef = useRef()
  useClickOutside(dropdownRef, () => {
    if (isSelectOpen) {
      setFilter('')
      setSelectOpen(false)
    }
  })
  const { yToast } = useYoloToast()

  const marketOpen = isStockMarketOpen().state

  const toggleSelect = () => {
    setSelectOpen((prev) => !prev)
  }

  const selectAsset = (coin, isMarketClose) => {
    if (isMarketClose) return
    setSelectOpen(false)
    // if (coin.type === ASSETS_TYPES.STOCKS && coin.type !== setActiveAsset.type) yToast(stockAssetRestrictionToastObj)
    if (activeAsset.type !== ASSETS_TYPES.STOCKS && coin.type === ASSETS_TYPES.STOCKS)
      yToast(stockAssetRestrictionToastObj)
    setActiveAsset(coin)
    setFilter('')
  }

  const onSelectAsset = (e, coin, isMarketClose) => {
    e.stopPropagation()
    selectAsset(coin, isMarketClose)
  }

  const onFilterChange = (e) => {
    const value = e.target.value.toUpperCase()
    if (value) setFilter(value)
    else setFilter('')
  }

  const filterBoxKeyPress = (e) => {
    switch (e.key) {
      case 'Enter':
        const firstAsset = hotAssets[0] || coldAssets[0]
        if (!firstAsset) {
          toggleSelect()
          break
        }
        const isMarketClose = firstAsset.hasTime && !marketOpen
        selectAsset(firstAsset, isMarketClose)
        break
      default:
        break
    }
  }

  const onSelectOpen = (e) => {
    e.stopPropagation()
    !noSelect && setSelectOpen(true)
  }

  const assetVariation = getAssetWithVariation(activeAsset)?.percentageChange

  return (
    <SelectWrapper onClick={onSelectOpen} noSelect={noSelect}>
      <Header>
        <SelectedAsset>
          <IconSelected className='asset_icon' icon={activeAsset.icon} />
          {!!activeAsset.icon2 && <IconSelected className='asset_icon' icon={activeAsset.icon2} />}
          <NameSelected className='asset_name'>{activeAsset.uiName}</NameSelected>
          {!noSelect && (
            <AssetChange className={`change ${Number(assetVariation) > 0 ? 'up' : 'down'}`}>
              {Number(assetVariation) ? `${assetVariation}%` : ''}
            </AssetChange>
          )}
        </SelectedAsset>
        {isSelectOpen && (
          <FilterBox
            type='text'
            placeholder='Search or select asset'
            autoFocus
            onChange={onFilterChange}
            onKeyPress={(e) => filterBoxKeyPress(e)}
          />
        )}

        {!noSelect && !isSelectOpen && <MenuSelect />}
      </Header>
      {/* <SelectDropdown isShow={isSelectOpen} onMouseLeave={() => onCloseSelect()} zIndex='9'> */}
      <SelectDropdown ref={dropdownRef} isShow={isSelectOpen} zIndex='9'>
        <>
          {/* FEATURED ITEMS */}
          {!isEmpty(featuredAssets) && <SelectDropdownTitle>FEATURED ⭐️</SelectDropdownTitle>}
          {featuredAssets.map((asset, idx) => {
            const isMarketClose = asset.hasTime && !marketOpen
            if (isMarketClose) return null
            const assetVariation = asset.percentageChange

            return (
              <SelectDropdownItem
                key={`featured-${idx}`}
                onClick={(e) => onSelectAsset(e, asset, isMarketClose)}
                disabled={isMarketClose}
              >
                <Icon icon={asset.icon} />
                {!!asset.icon2 && <Icon icon={asset.icon2} />}
                <Name>{asset.uiName}</Name>
                <AssetChange className={`change ${Number(assetVariation) > 0 ? 'up' : 'down'}`}>
                  {Number(assetVariation) ? `${assetVariation}%` : ''}
                </AssetChange>
              </SelectDropdownItem>
            )
          })}

          {/* HOT ITEMS */}
          {!isEmpty(hotAssets) && (
            <SelectDropdownTitle>
              HOT 🔥<HotPeriod>24h</HotPeriod>
            </SelectDropdownTitle>
          )}
          {hotAssets.map((asset, idx) => {
            const isMarketClose = asset.hasTime && !marketOpen
            const assetVariation = asset.percentageChange
            return (
              <SelectDropdownItem
                key={`hot-${idx}`}
                onClick={(e) => onSelectAsset(e, asset, isMarketClose)}
                disabled={isMarketClose}
              >
                <Icon icon={asset.icon} />
                {!!asset.icon2 && <Icon icon={asset.icon2} />}
                <Name>{asset.uiName}</Name>
                <AssetChange className={`change ${Number(assetVariation) > 0 ? 'up' : 'down'}`}>
                  {Number(assetVariation) ? `${assetVariation}%` : ''}
                </AssetChange>
              </SelectDropdownItem>
            )
          })}
        </>

        {assetsTypes.map((type, idx) => {
          return (
            <>
              <SelectDropdownTitle key={`ddT${idx}`}>
                {`${type}${type === ASSETS_TYPES.STOCKS ? 's' : ''}`.toUpperCase()}
              </SelectDropdownTitle>
              {coldAssets.map((asset, idx) => {
                if (asset.type !== type) return null
                const isMarketClose = type === asset.hasTime && !marketOpen
                const assetVariation = asset.percentageChange
                return (
                  <SelectDropdownItem
                    key={`ddi${idx}`}
                    onClick={(e) => onSelectAsset(e, asset, isMarketClose)}
                    disabled={isMarketClose}
                  >
                    <Icon icon={asset.icon} />
                    {!!asset.icon2 && <Icon icon={asset.icon2} />}
                    <Name>{asset.uiName}</Name>
                    <AssetChange className={`change ${Number(assetVariation) > 0 ? 'up' : 'down'}`}>
                      {Number(assetVariation) ? `${assetVariation}%` : ''}
                    </AssetChange>
                  </SelectDropdownItem>
                )
              })}
            </>
          )
        })}
      </SelectDropdown>
    </SelectWrapper>
  )
}
